import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCollectionById } from '../../services/apiService';
import { supabase } from '../../lib/supabase';
import ImageZoomOverlay from '../ImageZoomOverlay';
import ImageZoomOverlayMobile from '../ImageZoomOverlayMobile';
import UserSettingsModal from '../UserSettingsModal';
import UserSettingsModalMobile from '../UserSettingsModalMobile';
import ShareModal from '../ShareModal';
import './CollectionPage.css';

const CollectionPage = () => {
  const { collectionId } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showUserSettingsModal, setShowUserSettingsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const fetchCollection = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCollectionById(collectionId);
      if (response.success && response.data) {
        setCollection(response.data);
      } else {
        setError('Collection not found');
      }
    } catch (err) {
      console.error('Error fetching collection:', err);
      setError('Failed to load collection');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Get current user
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUser(user);
    });
    
    if (collectionId) {
      fetchCollection();
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [collectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="collection-page">
        <div className="collection-loading">Loading collection...</div>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="collection-page">
        <div className="collection-error">{error || 'Collection not found'}</div>
      </div>
    );
  }

  const images = collection.images || [];

  return (
    <div className="collection-page-container">
      {/* Header - Logo, Collection Info, User Profile */}
      <div className="collection-header">
        {/* Logo */}
        <div className="collection-header-logo">
          <img src="/KURA-logo.png" alt="KURA Logo" />
        </div>

        {/* Collection Info - Centered */}
        <div className="collection-header-info">
          {/* Row 1: Collection name */}
          <div className="collection-info-row collection-title-row">
            <h1 className="collection-title">{collection.name}</h1>
          </div>
        
          {/* Row 2: Image count */}
          <div className="collection-info-row">
            <span className="collection-count">{images.length} image{images.length !== 1 ? 's' : ''}</span>
          </div>
        
          {/* Row 3: Description */}
          {collection.description && (
            <div className="collection-info-row">
              <p className="collection-description">{collection.description}</p>
            </div>
          )}

          {/* Row 4: Owner info (for unauthenticated users or when viewing others' collections) */}
          {collection.owner && (!currentUser || collection.owner.id !== currentUser.id) && (
            <div className="collection-info-row collection-by-row">
              <img 
                src={collection.owner.avatarUrl || "/user-profile.png"} 
                alt="Owner" 
                className="collection-user-avatar"
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.target.src = "/user-profile.png";
                }}
              />
              <span className="collection-by-label">By</span>
              <span className="collection-user-name">{collection.owner.fullName}</span>
            </div>
          )}

          {/* Share button (available for all users) */}
          <div className="collection-info-row">
            <button 
              className="collection-share-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowShareModal(true);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              Share
            </button>
          </div>
        </div>

        {/* User Profile (only for authenticated users viewing their own collections) */}
        {currentUser && collection.owner && collection.owner.id === currentUser.id && (
          <div
            className="collection-header-profile"
            onClick={(e) => {
              e.stopPropagation();
              setShowUserSettingsModal(true);
            }}
            style={{ cursor: 'pointer' }}
          >
            <img 
              src={currentUser?.user_metadata?.avatar_url || "/user-profile.png"} 
              alt="User Profile" 
              onClick={(e) => {
                e.stopPropagation();
                setShowUserSettingsModal(true);
              }}
            />
          </div>
        )}
      </div>

      {/* Images Grid */}
      <div className="collection-content">
        {images.length === 0 ? (
          <div className="collection-empty">
            <p>This collection is empty</p>
          </div>
        ) : (
          <div className="collection-grid">
            {images.map((image, index) => (
              <div
                key={index}
                className="collection-image-card"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => setSelectedImage({ ...image, index })}
              >
                <img src={image.url} alt={`Collection ${index + 1}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedImage && (
        isMobile ? (
          <ImageZoomOverlayMobile
            imageUrl={selectedImage.url}
            imageData={{
              ...selectedImage,
              requestId: selectedImage.requestId || null,
            }}
            onClose={() => setSelectedImage(null)}
            mode="collection"
          />
        ) : (
          <ImageZoomOverlay
            imageUrl={selectedImage.url}
            imageData={{
              ...selectedImage,
              requestId: selectedImage.requestId || null,
            }}
            onClose={() => setSelectedImage(null)}
            mode="collection"
          />
        )
      )}

      {showUserSettingsModal && (
        isMobile ? (
          <UserSettingsModalMobile
            user={currentUser}
            onClose={() => setShowUserSettingsModal(false)}
          />
        ) : (
          <UserSettingsModal
            user={currentUser}
            onClose={() => setShowUserSettingsModal(false)}
          />
        )
      )}

      {showShareModal && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          isMobile={isMobile}
          collectionId={collectionId}
        />
      )}
    </div>
  );
};

export default CollectionPage;
